// Export tabulkových dat — CSV (Google Sheets / Excel) a PDF (přes tisk).
// Bez externí závislosti.

type Cell = string | number;

function csvEscape(v: Cell): string {
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Stáhne CSV (RFC, čárka jako oddělovač, UTF-8 BOM kvůli diakritice).
// Google Sheets i Excel ho otevřou přímo.
export function downloadCsv(filename: string, headers: string[], rows: Cell[][]): void {
  const lines = [headers, ...rows].map((r) => r.map(csvEscape).join(','));
  const csv = `﻿${lines.join('\r\n')}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function htmlEscape(v: Cell): string {
  return String(v).replace(
    /[&<>]/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[ch] ?? ch,
  );
}

// Otevři prázdné okno synchronně (kvůli popup blockeru — musí být v user gestu).
export function openBlankWindow(): Window | null {
  const w = window.open('', '_blank');
  if (w) {
    w.document.write(
      '<!doctype html><meta charset="utf-8"><body style="font-family:sans-serif">Připravuji…</body>',
    );
  }
  return w;
}

// Naplní (už otevřené) okno tabulkou a spustí tisk → uživatel uloží jako PDF.
export function fillPrintWindow(w: Window, title: string, headers: string[], rows: Cell[][]): void {
  const thead = `<tr>${headers.map((h) => `<th>${htmlEscape(h)}</th>`).join('')}</tr>`;
  const tbody = rows
    .map((r) => `<tr>${r.map((cn) => `<td>${htmlEscape(cn)}</td>`).join('')}</tr>`)
    .join('');
  const html = `<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>${htmlEscape(title)}</title>
<style>
  body{font-family:system-ui,-apple-system,sans-serif;margin:20px;color:#0f172a}
  h1{font-size:15px;margin:0 0 12px}
  table{border-collapse:collapse;width:100%;font-size:11px}
  th,td{border:1px solid #cbd5e1;padding:4px 7px;text-align:left;white-space:nowrap}
  th{background:#f1f5f9}
  td:nth-child(n+3),th:nth-child(n+3){text-align:right}
  @media print{@page{margin:14mm}}
</style></head>
<body><h1>${htmlEscape(title)}</h1>
<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>
<script>window.onload=function(){setTimeout(function(){window.print()},150)}</script>
</body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// Pohodlný wrapper: otevři okno a hned naplň (pro synchronní data).
export function printTable(title: string, headers: string[], rows: Cell[][]): void {
  const w = openBlankWindow();
  if (w) fillPrintWindow(w, title, headers, rows);
}

export type PlanMonth = {
  label: string;
  deadline: string;
  products: {
    name: string;
    code: string;
    image?: string | null;
    demand: number;
    revenue: number;
    sizes: { size: string; qty: number }[];
  }[];
};

// Plán přípravy do PDF (přes tisk): sekce po měsících, tabulka produktů s
// rozpadem velikostí. `withPrices` přidá sloupec obratu (pro střihárnu vypnout).
export function printPlanInWindow(
  w: Window,
  title: string,
  withPrices: boolean,
  months: PlanMonth[],
): void {
  const fmt = (n: number) => n.toLocaleString('cs-CZ');
  const sections = months
    .map((m) => {
      const head = withPrices
        ? '<tr><th class="img"></th><th>Produkt</th><th>Kód</th><th class="r">Obrat (Kč)</th><th class="r">Celkem ks</th><th>Rozpad velikostí</th></tr>'
        : '<tr><th class="img"></th><th>Produkt</th><th>Kód</th><th class="r">Celkem ks</th><th>Rozpad velikostí</th></tr>';
      const body = m.products
        .map((p) => {
          const sizes = p.sizes
            .map((s) => `${htmlEscape(s.size)}:&nbsp;<b>${fmt(s.qty)}</b>`)
            .join('&nbsp;&nbsp;');
          const img = p.image
            ? `<img class="thumb" src="${htmlEscape(p.image)}" alt="">`
            : '<div class="thumb"></div>';
          const tail = withPrices
            ? `<td class="r">${fmt(p.revenue)}</td><td class="r">${fmt(p.demand)}</td><td class="sizes">${sizes}</td>`
            : `<td class="r">${fmt(p.demand)}</td><td class="sizes">${sizes}</td>`;
          return `<tr><td class="img">${img}</td><td>${htmlEscape(p.name)}</td><td>${htmlEscape(p.code)}</td>${tail}</tr>`;
        })
        .join('');
      return `<h2>${htmlEscape(m.label)} <span class="dl">→ mít hotovo do ${htmlEscape(m.deadline)}</span></h2><table><thead>${head}</thead><tbody>${body}</tbody></table>`;
    })
    .join('');
  const html = `<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>${htmlEscape(title)}</title>
<style>
  body{font-family:system-ui,-apple-system,sans-serif;margin:18px;color:#0f172a}
  h1{font-size:16px;margin:0 0 6px}
  h2{font-size:13px;margin:18px 0 6px;border-bottom:2px solid #cbd5e1;padding-bottom:3px;page-break-after:avoid}
  .dl{font-weight:400;font-size:11px;color:#2563eb}
  table{border-collapse:collapse;width:100%;font-size:11px;margin-bottom:6px}
  th,td{border:1px solid #cbd5e1;padding:4px 7px;text-align:left;vertical-align:top}
  th{background:#f1f5f9}
  .r{text-align:right;white-space:nowrap}
  td.sizes{white-space:normal;line-height:1.7}
  td.img,th.img{width:34px;padding:3px}
  .thumb{height:30px;width:30px;object-fit:cover;border-radius:3px;background:#f1f5f9;display:block}
  @media print{@page{margin:12mm};.thumb{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head>
<body><h1>${htmlEscape(title)}</h1>${sections}
<script>
(function(){
  function go(){window.print();}
  var imgs=document.images, n=imgs.length, done=0;
  if(!n){window.onload=function(){setTimeout(go,150);};return;}
  function tick(){if(++done>=n)setTimeout(go,80);}
  for(var i=0;i<n;i++){if(imgs[i].complete)tick();else{imgs[i].onload=tick;imgs[i].onerror=tick;}}
})();
</script>
</body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
